/**
 * Pure helpers for the domain monogram tile (see `DomainMonogram.tsx`).
 *
 * Kept framework-free so the initials + tone logic can be unit-tested in
 * isolation. Distinct from `avatar.ts`, which builds circular *people* avatars
 * with a saturated palette; domain monograms are rounded squares in a muted
 * low-chroma palette so they sit quietly in the grey sidebar.
 */

/** Number of monogram tones; must match the `--color-monogram-{n}-{bg,fg}` token sets. */
export const MONOGRAM_COUNT = 6;

// Words are separated by whitespace, hyphen or underscore (e.g. "a-b_c" -> a,b,c).
const WORD_SEPARATOR = /[\s\-_]+/;
// A "meaningful" glyph for initials: any Unicode letter or number.
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/**
 * Split a string into grapheme clusters so multi-code-point glyphs stay intact.
 *
 * `Array.from` splits by Unicode code point, which slices ZWJ / family / flag
 * emoji sequences mid-glyph. Prefer `Intl.Segmenter` (grapheme granularity)
 * where available, falling back to code-point splitting otherwise.
 *
 * `Intl.Segmenter` isn't typed by this project's TS lib (`esnext` omits
 * `es2022.intl`), so it's accessed via a narrow structural cast rather than
 * `any`.
 */
const toGlyphs = (value: string): string[] => {
  const SegmenterCtor = (
    Intl as unknown as {
      Segmenter?: new (
        locales?: string,
        options?: { granularity: 'grapheme' },
      ) => { segment(input: string): Iterable<{ segment: string }> };
    }
  ).Segmenter;
  if (typeof SegmenterCtor === 'function') {
    const segmenter = new SegmenterCtor(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(value), (part) => part.segment);
  }
  return Array.from(value);
};

/** First letter-or-digit glyph of a word, falling back to its first glyph. */
const leadGlyph = (word: string): string => {
  const glyphs = toGlyphs(word);
  return glyphs.find((glyph) => LETTER_OR_DIGIT.test(glyph)) ?? glyphs[0] ?? '';
};

/**
 * Uppercase initials for a domain name.
 *
 * - Two+ words: first letter-or-digit of the first two words ("My Domain" -> "MD").
 * - Single word: first two glyphs, preferring letters/digits ("Docs" -> "DO").
 * - Empty / whitespace-only: "?".
 *
 * Uses `Array.from` throughout so emoji / non-Latin names never split a glyph.
 */
export const domainInitials = (name: string | null | undefined): string => {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return '?';

  const words = trimmed.split(WORD_SEPARATOR).filter(Boolean);
  if (words.length === 0) return '?';

  if (words.length === 1) {
    const glyphs = toGlyphs(words[0]);
    const alnum = glyphs.filter((glyph) => LETTER_OR_DIGIT.test(glyph));
    const picked = (alnum.length >= 2 ? alnum : glyphs).slice(0, 2);
    return (picked.join('') || '?').toUpperCase();
  }

  return (leadGlyph(words[0]) + leadGlyph(words[1])).toUpperCase() || '?';
};

/**
 * Deterministic tone index (1..MONOGRAM_COUNT) for a stable seed (the site_id).
 *
 * FNV-1a 32-bit hash so the same domain always gets the same colour. Returns a
 * 1-based index to line up with the `--color-monogram-1-bg` .. `-6-` token names.
 */
export const monogramToneIndex = (seed: string | null | undefined): number => {
  const value = seed ?? '';
  let hash = 0x811c9dc5; // FNV-1a offset basis
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0; // multiply by FNV prime, keep 32-bit
  }
  return (hash % MONOGRAM_COUNT) + 1;
};
